import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { LayoutDashboard, Users, MemoryStick, Lightbulb, Heart, HelpCircle, Mail, FileText } from "lucide-react";
import { useState, useEffect } from "react";

const DashboardSidebar = () => {
  const location = useLocation();
  const [quote, setQuote] = useState({ text: "", author: "" });

  const navItems = [
    { name: "Home", path: "/dashboard", icon: LayoutDashboard },
    { name: "People", path: "/people", icon: Users },
    { name: "Memories", path: "/memories", icon: MemoryStick },
    { name: "Suggestions", path: "/suggestions", icon: Lightbulb },
  ];

  const quotes = [
    { text: "The most precious gift we can offer anyone is our attention.", author: "Thich Nhat Hanh" },
    { text: "Connection is why we're here; it gives purpose and meaning to our lives.", author: "Brené Brown" },
    { text: "In every walk with nature, one receives far more than he seeks.", author: "John Muir" },
    { text: "The little things? The little moments? They aren't little.", author: "Jon Kabat-Zinn" },
    { text: "We are shaped by our thoughts; we become what we think.", author: "Buddha" },
    { text: "Relationship is the mirror in which you discover yourself.", author: "Jiddu Krishnamurti" },
    { text: "The quality of your life is the quality of your relationships.", author: "Tony Robbins" },
    { text: "Remember that the most valuable antiques are dear old friends.", author: "H. Jackson Brown Jr." },
  ];

  useEffect(() => {
    // Pick a random quote on mount
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setQuote(randomQuote);
  }, []);

  return (
    <Sidebar className="bg-white border-none">
      <SidebarHeader className="p-4 pt-8 pb-8 bg-white">
        <Link to="/dashboard" className="flex items-center">
          <img
            src="/images/logo4.png"
            alt="Wabisabi Logo"
            className="w-[70px] h-[70px]"
          />
        </Link>
      </SidebarHeader>
      <SidebarContent className="bg-white px-0">
        <div className="mb-4 mt-4">
          <h3 className="px-6 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Menu
          </h3>
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-6 py-3 transition-colors w-full ${
                    isActive
                      ? "bg-wabisabi-plum text-white"
                      : "text-gray-700 hover:bg-wabisabi-plum/10"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </SidebarContent>
      <SidebarFooter className="bg-white px-4 pb-4">
        {/* Inspirational Quote */}
        {quote.text && (
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
            <div className="flex items-start gap-2">
              <Heart className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-700 italic leading-relaxed mb-1">
                  "{quote.text}"
                </p>
                <p className="text-xs text-purple-600 font-medium">— {quote.author}</p>
              </div>
            </div>
          </div>
        )}

        {/* Help & Support Link */}
        <Link
          to="/contact"
          className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-gray-600 hover:text-wabisabi-plum hover:bg-gray-50 rounded transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          Help & Support
        </Link>

        {/* Footer Text */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Made with 💜 by Wabisabi
          </p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;
